import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Col, Divider, Row, Space, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'components/Icon';
import {
  isUndefined as _isUndefined,
  isEmpty as _isEmpty,
  map as _map,
  find as _find,
  delay as _delay
} from 'lodash-es';
import './GraasChromeExtensionProcess.scss';
import moment from 'moment-timezone';
import { mouseflowTrack } from 'pages/Dashboard/utility';
import { useTranslation } from 'react-i18next';
import {
  getNickNameValue,
  getChannelExpiryResultMap,
  upgradeChannelAccount,
  chormeExtensionHanldedChannel
} from 'utilities/utilities_exports';
import { LinkOutlined } from '@ant-design/icons';
import { onUpdateUser } from 'redux/BusinessProfile/BusinessProfile.action';
import { apiServices } from 'redux/utils/api_services';
import { getDataFromApiAction } from 'components/Atoms/GraasApiUtility/GraasApiUtility.action';
import { getSellerCentreURL } from 'Modules/Connect/components/ConnectStoreList/utility';
import queryString from 'query-string';
import SMResult from 'components/SMResult/SMResult';

const ConnectJournalDrawer = lazy(() =>
  import(
    /* webpackChunkName: 'ConnectJournalDrawer' */ 'Modules/Connect/components/ConnectJournalDrawer/ConnectJournalDrawer'
  )
);

const maxChromeExtensionApiCount = 10;

const GraasChromeExtensionProcess = ({ isChromeExtensionInstalled, onCloseExtensionModal, actionRequiredChannel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState(true);
  const nicknames = useSelector(state => state.Account?.stores?.nicknames);
  const accountDetails = useSelector(state => state.User?.accountDetails || []);
  const storeStepDetails = useSelector(state => state?.ConnectJournal?.global);
  const [isRemainderMeLater, setIsRemainderMeLater] = useState(false);
  const userDetails = useSelector(state => state?.User?.accountDetails?.userDetails);
  const userData = useSelector(state => state?.User?.userData || []);
  const [openDrawer, setOpenDrawer] = useState(false);
  const hasWriteAccess = useSelector(state => state?.Account?.hasWriteAccess);
  const [channelExpiryMap, setChannelExpiryMap] = useState({});

  useEffect(() => {
    if (!_isEmpty(accountDetails)) {
      setChannelExpiryMap(getChannelExpiryResultMap(accountDetails));
    }
  }, [dispatch, accountDetails]);

  const redirectToChromeExtension = text => {
    handledOpen(false);
    window.open(
      `${process.env.REACT_APP_CHROME_EXTENSION_AUTO_REPORT_DOWNLOAD_LINK}${
        !_isEmpty(text) ? '#:~:text=Overview' : ''
      }`,
      '_blank'
    );
    checkChromeExtensionInstalled(1);
    mouseflowTrack(
      {
        username: userData?.userName,
        email: accountDetails?.userDetails?.SELLinALLAuth?.SELLinALLId || '',
        storeCode: accountDetails?.merchantID || '',
        isDemoAccount: !hasWriteAccess
      },
      'CHROME_EXTENSION_GLOBAL_HEADER_REDIRECT_CLICKED'
    );
  };

  // API call to fetch ChromeExtension Flag enabled or not
  const getChromeExtensionFlags = useCallback(async count => {
    const apiUrl = `${apiServices['account']}/services/accounts?merchantIds=${userData?.merchantID}&filterfields=isChromeExtensionInstalled`;
    const apiResponse = await getDataFromApiAction(apiUrl, {});
    if (apiResponse?.accounts && apiResponse?.accounts?.length > 0) {
      let accountsObj = apiResponse?.accounts[0];
      if (_isUndefined(accountsObj?.isChromeExtensionInstalled) || !accountsObj?.isChromeExtensionInstalled) {
        checkChromeExtensionInstalled(count);
      }
    } else {
      checkChromeExtensionInstalled(count);
    }
  });

  const checkChromeExtensionInstalled = count => {
    if (count <= maxChromeExtensionApiCount) {
      _delay(() => {
        if (document.visibilityState === 'visible') {
          getChromeExtensionFlags(count + 1);
        } else {
          checkChromeExtensionInstalled(count + 1);
        }
      }, 5000); // 5000 milliseconds = 5 seconds
    }
  };

  const redirectToLogin = redirect_url => {
    window.open(redirect_url, '_blank');
    mouseflowTrack(
      {
        username: userData?.userName,
        email: accountDetails?.userDetails?.SELLinALLAuth?.SELLinALLId || '',
        storeCode: accountDetails?.merchantID || '',
        isDemoAccount: !hasWriteAccess
      },
      'SELLER_CENTER_REDIRECT_CLICKED'
    );
  };

  const getActionButton = (redirect_url, nickNameID) => {
    if (!_isEmpty(channelExpiryMap) && channelExpiryMap?.[nickNameID]) {
      return (
        <>
          <button
            className='c-button is-link no-pad'
            style={{ cursor: hasWriteAccess ? 'pointer' : 'no-drop' }}
            onClick={() =>
              hasWriteAccess && upgradeChannelAccount(nickNameID, channelExpiryMap?.[nickNameID], dispatch)
            }
          >
            <Space>{t('GRAAS_REAUTHENTICATE_GRAAS')}</Space>
          </button>
        </>
      );
    }
    let channel = nickNameID.split('-')?.[0] || '';
    if (!isChromeExtensionInstalled && chormeExtensionHanldedChannel.includes(channel)) {
      return (
        <>
          <button
            className='c-button is-link no-pad'
            style={{ cursor: hasWriteAccess ? 'pointer' : 'no-drop' }}
            onClick={() => hasWriteAccess && redirectToChromeExtension()}
          >
            <Space>
              {t('GRAAS_INSTALL_CHROME_EXTENSION_GRAAS')}
              <LinkOutlined />
            </Space>
          </button>
        </>
      );
    }
    if (!chormeExtensionHanldedChannel.includes(channel)) {
      return (
        <>
          <button
            className='c-button is-link no-pad'
            style={{ cursor: hasWriteAccess ? 'pointer' : 'no-drop' }}
            onClick={() => hasWriteAccess && window.productFruits.api.feedback.showModal()}
          >
            <Space>{t('GRAAS_REPORT_AN_ISSUE_GRAAS')}</Space>
          </button>
        </>
      );
    }
    return (
      <>
        <button
          className='c-button is-link no-pad'
          style={{ cursor: hasWriteAccess ? 'pointer' : 'no-drop' }}
          onClick={() => hasWriteAccess && redirectToLogin(redirect_url)}
        >
          <Space>
            {t('GRAAS_LOGIN_GRAAS')}
            <LinkOutlined />
          </Space>
        </button>
      </>
    );
  };

  const getProgressStoreDetails = over_all_processed => {
    return (
      <>
        <div style={{ minHeight: '80px' }}>
          {_map(over_all_processed, (summaryObj, index) => {
            let redirect_url = '';
            let find_nickName = _find(nicknames, i => i?.id === summaryObj?.nickNameID) || {};
            let channel = find_nickName?.id?.split('-')?.[0] || '';
            let currencyCode = find_nickName?.currencyCode || '';
            if (getSellerCentreURL?.[channel.toUpperCase()]?.[currencyCode]) {
              redirect_url = getSellerCentreURL?.[channel.toUpperCase()]?.[currencyCode];
            }
            if (index < 3) {
              return (
                <>
                  <div style={{ paddingBottom: '7px' }}>
                    <Row>
                      <Col md={12}>
                        <span className='c-heading smm mb-0 color-light'>
                          {summaryObj?.nickNameID.split('-')[0] +
                            '-' +
                            getNickNameValue(nicknames, summaryObj?.nickNameID)}
                        </span>
                      </Col>
                      <Col md={12} className='text-right'>
                        {getActionButton(redirect_url, summaryObj?.nickNameID)}
                      </Col>
                    </Row>
                    <Row>
                      <span className='c-heading sm mb-1 color-light'>
                        {t('GRAAS_LAST_LOGGED_IN_GRAAS')} &nbsp;
                        {summaryObj?.lastReportUpdatedTime || 'NA'}
                      </span>
                    </Row>
                  </div>
                </>
              );
            }
          })}
        </div>
      </>
    );
  };

  const handledOpen = value => {
    if (isRemainderMeLater) {
      const payload = {
        timeTillChromeInfoDisabled: parseInt(moment().add(2, 'days').valueOf() / 1000),
        userId: userDetails?.userId
      };
      const responseData = dispatch(onUpdateUser(payload));
      responseData.then(response => {
        if (response?.hasError) {
          message.error(t('GRAAS_USER_UPDATE_FAIL_MESSAGE_GRAAS'));
        } else if (response?.data?.response === 'failure') {
          message.warning(responseData?.data?.responseMessage || t('GRAAS_USER_UPDATE_FAIL_MESSAGE_GRAAS'));
        } else {
          setInProgress(false);
          message.success(t('GRAAS_USER_UPDATE_SUCCESS_MESSAGE_GRAAS'));
        }
      });
    }
  };

  useEffect(() => handledOpen(true), []);

  const handledRedirect = () => {
    handledOpen(false);
    const parsed = queryString.parse(window.location.search);
    const existingQueryParams = { ...parsed, connectJournal: 'drawer' };
    const queryParams = new URLSearchParams(existingQueryParams).toString();
    window.history.replaceState(null, null, `${window.location.origin}${window.location.pathname}?${queryParams}`);
    setOpenDrawer(true);
  };

  const renderDataChromeContent = () => {
    return (
      <>
        <div className='dataWrapper'>
          <Row>
            <Col md={16}>
              <span className='c-heading color-white smm'> {t('GRAAS_ENABLE_DATA_EXTRACTION_GRAAS')}</span>
            </Col>
            <Col md={8} className='text-right'>
              <button
                className='c-button isClean no-pad'
                onClick={() => {
                  handledOpen(false);
                  onCloseExtensionModal();
                }}
              >
                <Icon name='close' size='default' />
              </button>
            </Col>
          </Row>
          <button className='c-button is-clean text-left width-10'>
            <Row>
              <Col md={24}>
                <span className='c-heading sm mb-0 color-light dont-break'>
                  {t('GRAAS_DATA_EXTRACTION_DESCRIPTION_GRAAS')}
                </span>
              </Col>
            </Row>
          </button>
          <Row>
            <Col md={4}></Col>
            <Col md={20} className='text-right'>
              <button
                className='c-button  '
                style={{ cursor: hasWriteAccess ? 'pointer' : 'no-drop' }}
                onClick={() => hasWriteAccess && redirectToChromeExtension()}
              >
                <Space>
                  {t('GRAAS_INSTALL_CHROME_EXTENSION_GRAAS')}
                  <LinkOutlined />
                </Space>
              </button>
            </Col>
          </Row>
        </div>
      </>
    );
  };

  // !-- Render content
  const renderDataContent = () => {
    return (
      <>
        <div className='dataWrapper chrome_extension_wrapper'>
          <div className='dataWrapper_head'>
            <Row>
              <Col md={16}>
                <span className='c-heading color-white smm'>{t('GRAAS_DATA_DOCTOR_GRAAS')}</span>
              </Col>
              <Col md={8} className='text-right'>
                <button
                  className='c-button isClean no-pad'
                  onClick={() => {
                    handledOpen(false);
                    onCloseExtensionModal();
                  }}
                >
                  <Icon name='close' size='default' />
                </button>
              </Col>
            </Row>
          </div>
          <div className={`dataWrapper_${actionRequiredChannel?.length > 0 ? 'body' : 'smBody'}`}>
            {actionRequiredChannel?.length > 0 ? (
              <button className='c-button is-clean text-left width-100'>
                <Row>
                  <Col md={24}>
                    <span className='c-heading smm mb-0' style={{ color: '#FEC84B' }}>
                      {actionRequiredChannel?.length} {t('GRAAS_CHANNELS_NEED_ATTENTION_GRAAS')}
                    </span>
                    <span className='c-heading sm mb-0 color-light dont-break'>
                      {t(
                        'GRAAS_AFFECTED_METRICS_CONVERSION_RATE_TRAFFIC_AND_PROMOTION_PLEASE_LOGIN_IN_TO_SELLER_CENTER_TO_ENABLE_AUTO_DOWNLOAD_GRAAS'
                      )}
                    </span>
                  </Col>
                </Row>
              </button>
            ) : (
              <>
                <Row
                  className='c-heading sm mb-0 color-light dont-break'
                  justify={!storeStepDetails?.isDataAvailable ? 'center' : ''}
                >
                  {storeStepDetails?.loading ? (
                    <SMResult type='loader' />
                  ) : !storeStepDetails?.isDataAvailable ? (
                    t('GRAAS_DATA_DOCTOR_METRIC_DATA_SYNC_MSG_GRAAS')
                  ) : (
                    t('GRAAS_DATA_DOCTOR_METRIC_SUCCESS_MSG_GRAAS')
                  )}
                </Row>
              </>
            )}
            {actionRequiredChannel?.length > 0 && <Divider style={{ backgroundColor: '#98a2b3', margin: '10px 0' }} />}
            {storeStepDetails?.loading && !storeStepDetails?.error && actionRequiredChannel?.length > 0 && (
              <SMResult type='loader' />
            )}
            {!storeStepDetails?.loading &&
              !storeStepDetails?.error &&
              actionRequiredChannel?.length > 0 &&
              getProgressStoreDetails(actionRequiredChannel)}
            {actionRequiredChannel?.length > 0 && <Divider style={{ backgroundColor: '#98a2b3', margin: '10px 0' }} />}
            <Row>
              <Col md={16}>
                {/* <Checkbox
                  onChange={e => setIsRemainderMeLater(e.target.checked)}
                  checked={isRemainderMeLater}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <span className='c-heading color-light smm'>{t('GRAAS_REMIND_ME_IN_TWO_DAYS_GRAAS')}.</span>
                </Checkbox> */}
              </Col>
              <Col
                md={8}
                className='text-right'
                style={{
                  display: storeStepDetails?.isDataAvailable ? 'block' : 'none'
                }}
              >
                <button className='c-button is-link no-pad ' onClick={() => handledRedirect()}>
                  {t('GRAAS_MORE_DETAILS_GRAAS')}
                  <Icon name='keyboard_arrow_right' size='default' />
                </button>
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {});
    return () => {
      document.removeEventListener('visibilitychange', () => {});
    };
  }, []);

  return (
    <>
      <div className='custom_toolTip isModal'>{renderDataContent()}</div>
      {openDrawer && (
        <Suspense fallback={<></>}>
          <ConnectJournalDrawer
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            actionRequiredChannel={actionRequiredChannel}
          />
        </Suspense>
      )}
    </>
  );
};

export default GraasChromeExtensionProcess;
